/**
 * useEmailModules Hook
 *
 * Get module list, toggle modules, and expose feature flags via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailAccountModule } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useEmailModules(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: [...queryKeys.email.all, 'modules', accountId],
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailAccountModules(accountId);
    },
    enabled: !!accountId,
  });

  const modules = queryResult.data?.modules ?? ([] as EmailAccountModule[]);

  const isModuleEnabled = useCallback(
    (moduleName: string): boolean => {
      const mod = modules.find((m) => m.moduleName === moduleName);
      return mod?.enabled ?? false;
    },
    [modules],
  );

  const toggleModule = useCallback(
    async (moduleName: string, enabled: boolean) => {
      if (!accountId) return;
      const result = await api.updateEmailAccountModule(accountId, moduleName, { enabled });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.email.all, 'modules', accountId],
      });
      return result;
    },
    [accountId, queryClient],
  );

  const updateModuleConfig = useCallback(
    async (moduleName: string, config: Record<string, unknown>) => {
      if (!accountId) return;
      const result = await api.updateEmailAccountModule(accountId, moduleName, { config });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.email.all, 'modules', accountId],
      });
      return result;
    },
    [accountId, queryClient],
  );

  return {
    modules,
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    isModuleEnabled,
    toggleModule,
    updateModuleConfig,
  };
}
